import React, { useState, useEffect } from 'react';
import { TargetSelect } from './TargetSelect';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const OSINTScanner = () => {
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [scans, setScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [viewMode, setViewMode] = useState('form'); // 'form' | 'results' | 'history'

  // Opciones de scan
  const [options, setOptions] = useState({
    whois: true,
    dns: true,
    subdomains: true,
    emails: true
  });

  useEffect(() => {
    fetchScans();
  }, []);

  const fetchScans = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/scans/osint`, { headers });
      setScans(response.data);
    } catch (error) {
      console.error("Error fetching OSINT scans:", error);
    }
  };

  const startScan = async (e) => {
    e.preventDefault();
    if (!domain) {
      alert("Por favor ingresa un dominio");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/scans/osint`, {
        domain: domain,
        options: options
      }, { headers }, { headers });
      
      setSelectedScan(response.data);
      setViewMode('results');
      fetchScans();
      setDomain('');
    } catch (error) {
      alert("Error ejecutando OSINT scan: " + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const viewScanDetails = (scan) => {
    setSelectedScan(scan);
    setViewMode('results');
  };

  return (
    <div className="space-y-6">
      {/* Navigation Tabs */}
      <div className="bg-white rounded-lg shadow-lg p-4">
        <div className="flex space-x-4 border-b">
          <button
            onClick={() => setViewMode('form')}
            className={`px-4 py-2 font-medium ${
              viewMode === 'form'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            🔍 Nuevo Scan OSINT
          </button>
          <button
            onClick={() => setViewMode('history')}
            className={`px-4 py-2 font-medium ${
              viewMode === 'history'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            📋 Historial ({scans.length})
          </button>
          {selectedScan && (
            <button
              onClick={() => setViewMode('results')}
              className={`px-4 py-2 font-medium ${
                viewMode === 'results'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              📊 Resultados
            </button>
          )}
        </div>
      </div>

      {/* Form View */}
      {viewMode === 'form' && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <span className="mr-2">🕵️</span>
            Escaneo OSINT - Open Source Intelligence
          </h2>

          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>OSINT</strong> recopila información pública sobre un dominio:
              WHOIS, registros DNS, subdominios, emails expuestos, etc.
            </p>
          </div>

          <form onSubmit={startScan} className="space-y-6">
            <TargetSelect
              label="Dominio a escanear"
              value={domain}
              onChange={setDomain}
              types={['domain']}
              helpText="Solo dominios con autorización de escaneo vigente."
              testid="osint-target-select"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Opciones de escaneo
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={options.whois}
                    onChange={(e) => setOptions({ ...options, whois: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">WHOIS Lookup</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={options.dns}
                    onChange={(e) => setOptions({ ...options, dns: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">DNS Records</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={options.subdomains}
                    onChange={(e) => setOptions({ ...options, subdomains: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Subdominios</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={options.emails}
                    onChange={(e) => setOptions({ ...options, emails: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Email Harvesting</span>
                </label>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !domain}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
            >
              {loading ? "🔄 Escaneando..." : "🚀 Iniciar Escaneo OSINT"}
            </button>
          </form>
        </div>
      )}

      {/* History View */}
      {viewMode === 'history' && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Historial de Escaneos OSINT
          </h3>

          {scans.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No hay escaneos OSINT registrados aún.</p>
              <button
                onClick={() => setViewMode('form')}
                className="mt-4 text-blue-600 hover:underline"
              >
                Crear primer escaneo →
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {scans.map((scan) => (
                <div
                  key={scan.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-400 cursor-pointer transition"
                  onClick={() => viewScanDetails(scan)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">{scan.domain}</h4>
                      <p className="text-sm text-gray-500">
                        {new Date(scan.created_at).toLocaleString('es-AR')}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="inline-block px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                        ✓ {scan.status}
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        {scan.results?.summary?.successful_checks || 0}/
                        {scan.results?.summary?.total_checks || 0} checks
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Results View */}
      {viewMode === 'results' && selectedScan && (
        <OSINTResults scan={selectedScan} />
      )}
    </div>
  );
};

// Component to display OSINT results
const OSINTResults = ({ scan }) => {
  const [activeTab, setActiveTab] = useState('summary');

  const results = scan.results || {};
  const summary = results.summary || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{scan.domain}</h2>
            <p className="text-sm text-gray-500">
              Escaneado el {new Date(scan.created_at).toLocaleString('es-AR')}
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-blue-600">
              {summary.successful_checks || 0}/{summary.total_checks || 0}
            </div>
            <p className="text-xs text-gray-500">Checks exitosos</p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-600">
              {results.whois?.success ? '✓' : '✗'}
            </div>
            <p className="text-sm text-gray-700 mt-1">WHOIS</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-600">
              {results.dns_records?.data ? Object.keys(results.dns_records.data).length : 0}
            </div>
            <p className="text-sm text-gray-700 mt-1">DNS Records</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-purple-600">
              {results.subdomains?.data?.found_count || 0}
            </div>
            <p className="text-sm text-gray-700 mt-1">Subdominios</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-4">
            <div className="text-2xl font-bold text-orange-600">
              {results.emails?.data?.count || 0}
            </div>
            <p className="text-sm text-gray-700 mt-1">Emails</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-lg">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-4 px-6">
            {['summary', 'whois', 'dns', 'subdomains', 'emails'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-3 font-medium text-sm border-b-2 ${
                  activeTab === tab
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Summary Tab */}
          {activeTab === 'summary' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Resumen General</h3>
              <div className="grid gap-4">
                {results.whois?.success && (
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium mb-2">📋 WHOIS Information</h4>
                    <dl className="grid grid-cols-2 gap-2 text-sm">
                      <dt className="text-gray-600">Registrar:</dt>
                      <dd className="font-medium">{results.whois.data?.registrar || 'N/A'}</dd>
                      <dt className="text-gray-600">Creado:</dt>
                      <dd className="font-medium">{results.whois.data?.creation_date || 'N/A'}</dd>
                      <dt className="text-gray-600">Expira:</dt>
                      <dd className="font-medium">{results.whois.data?.expiration_date || 'N/A'}</dd>
                    </dl>
                  </div>
                )}
                
                {results.subdomains?.data?.found_count > 0 && (
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium mb-2">🌐 Subdominios Encontrados</h4>
                    <p className="text-2xl font-bold text-purple-600">
                      {results.subdomains.data.found_count}
                    </p>
                    <p className="text-sm text-gray-500">
                      de {results.subdomains.data.scanned_count} escaneados
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* WHOIS Tab */}
          {activeTab === 'whois' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">WHOIS Data</h3>
              {results.whois?.success ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-600">Registrar</label>
                      <p className="font-medium">{results.whois.data?.registrar || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Status</label>
                      <p className="font-medium">{results.whois.data?.status || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Fecha de Creación</label>
                      <p className="font-medium">{results.whois.data?.creation_date || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Fecha de Expiración</label>
                      <p className="font-medium">{results.whois.data?.expiration_date || 'N/A'}</p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm text-gray-600">Name Servers</label>
                    <ul className="mt-2 space-y-1">
                      {(results.whois.data?.name_servers || []).map((ns, idx) => (
                        <li key={idx} className="text-sm bg-gray-50 px-3 py-1 rounded">
                          {ns}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <label className="text-sm text-gray-600">Raw WHOIS Data</label>
                    <pre className="mt-2 text-xs bg-gray-900 text-green-400 p-4 rounded-lg overflow-auto max-h-96">
                      {results.whois.data?.raw_data || 'No data'}
                    </pre>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">Error obteniendo datos WHOIS: {results.whois?.error}</p>
              )}
            </div>
          )}

          {/* DNS Tab */}
          {activeTab === 'dns' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">DNS Records</h3>
              {results.dns_records?.success ? (
                <div className="space-y-4">
                  {Object.entries(results.dns_records.data || {}).map(([type, records]) => (
                    <div key={type} className="border rounded-lg p-4">
                      <h4 className="font-medium text-blue-600 mb-2">{type} Records</h4>
                      {records.length > 0 ? (
                        <ul className="space-y-1">
                          {records.map((record, idx) => (
                            <li key={idx} className="text-sm bg-gray-50 px-3 py-2 rounded font-mono">
                              {record}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500">No hay registros {type}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">Error obteniendo DNS records: {results.dns_records?.error}</p>
              )}
            </div>
          )}

          {/* Subdomains Tab */}
          {activeTab === 'subdomains' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">
                Subdominios Descubiertos ({results.subdomains?.data?.found_count || 0})
              </h3>
              {results.subdomains?.success ? (
                <div className="space-y-2">
                  {(results.subdomains.data?.subdomains || []).map((sub, idx) => (
                    <div key={idx} className="border rounded-lg p-4 hover:border-blue-400 transition">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">{sub.subdomain}</h4>
                          <p className="text-sm text-gray-500">
                            {sub.ips.join(', ')}
                          </p>
                        </div>
                        <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                          {sub.status}
                        </span>
                      </div>
                    </div>
                  ))}
                  
                  {results.subdomains.data?.found_count === 0 && (
                    <p className="text-center py-8 text-gray-500">
                      No se encontraron subdominios activos
                    </p>
                  )}
                  
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
                    <p>
                      Escaneados: {results.subdomains.data?.scanned_count || 0} subdominios comunes | 
                      Encontrados: {results.subdomains.data?.found_count || 0} activos
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">Error descubriendo subdominios: {results.subdomains?.error}</p>
              )}
            </div>
          )}

          {/* Emails Tab */}
          {activeTab === 'emails' && (
            <div>
              <h3 className="text-lg font-semibold mb-4">
                Emails Encontrados ({results.emails?.data?.count || 0})
              </h3>
              {results.emails?.success ? (
                <div>
                  {results.emails.data?.count > 0 ? (
                    <ul className="space-y-2">
                      {(results.emails.data.emails || []).map((email, idx) => (
                        <li key={idx} className="bg-gray-50 px-4 py-3 rounded-lg">
                          <span className="font-mono text-sm">{email}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No se encontraron emails públicos en el sitio web</p>
                      <p className="text-xs mt-2">
                        Esto es común en sitios que no exponen emails directamente en el HTML
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">Error buscando emails: {results.emails?.error}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OSINTScanner;

